import { onMounted, onUnmounted, watch, type Ref } from 'vue'

import type { AnalysisRun, CausalProgressEvent, RunPhase } from '../models/impact.types'
import { parseCausalProgressEvent } from '../models/progressEvent'
import { useImpactStore } from '../stores/impactStore'

import { useWorkspace } from '@/features/account'
import { useCreditGuard } from '@/features/credits'
import { useI18n } from '@/shared/i18n'
import { useToast } from '@/shared/ui'

export interface ImpactEventBus {
  on: (name: 'causal_single_progress', handler: (payload: unknown) => void) => () => void
}

interface VisibilityDocument {
  readonly visibilityState: DocumentVisibilityState
  addEventListener: Document['addEventListener']
  removeEventListener: Document['removeEventListener']
}

/**
 * How long a pending/running execution may go without any `causal_single_progress` frame before the
 * runtime asks the backend for its real state (ledger §C 5a). Not a deadline on the analysis: a
 * run the backend still reports as pending/running (a long queue) is left as it is and asked
 * again after the same quiet period.
 */
export const SILENCE_TIMEOUT_MS = 30 * 60 * 1000

type Timer = ReturnType<typeof setTimeout>

export interface CausalExecutionRuntimeDeps {
  bus: ImpactEventBus
  workspaceId: () => number | string | undefined
  isWizardOpen: () => boolean
  isRunActive: () => boolean
  /** The execution the store holds as pending/running, or `null` when none is. */
  activeExecutionId: () => string | null
  /** That execution's row from the last `/results/` fetch. */
  findRun: (executionId: string) => AnalysisRun | null
  applyProgressEvent: (event: CausalProgressEvent) => void
  refreshRunsAndSchedules: () => Promise<void>
  refreshQuota: () => Promise<void>
  refreshCredits: () => Promise<void>
  dismissOverlay: () => void
  notifyCompleted: (name: string | null) => void
  notifyFailed: (message: string | null) => void
  document?: VisibilityDocument
  silenceTimeoutMs?: number
  setTimer?: (callback: () => void, ms: number) => Timer
  clearTimer?: (timer: Timer) => void
}

export interface CausalExecutionRuntime {
  start: () => void
  stop: () => void
  syncWatchdog: () => void
  whenIdle: () => Promise<void>
}

let installedBus: ImpactEventBus | null = null

/** Composition-root injection: feature code never imports the app shell. */
export function registerImpactEventBus(bus: ImpactEventBus): void {
  installedBus = bus
}

export function resetImpactEventBusForTest(): void {
  installedBus = null
}

function requiredBus(): ImpactEventBus {
  if (!installedBus) throw new Error('Impact event bus must be registered before page mount')
  return installedBus
}

/** Framework-light runtime so workspace filtering, terminal de-dup and visibility recovery mutate. */
export function createCausalExecutionRuntime(
  deps: CausalExecutionRuntimeDeps,
): CausalExecutionRuntime {
  const terminals = new Set<string>()
  const pending = new Set<Promise<void>>()
  const visibilityDocument = deps.document ?? document
  const silenceTimeoutMs = deps.silenceTimeoutMs ?? SILENCE_TIMEOUT_MS
  const setTimer = deps.setTimer ?? ((callback, ms) => setTimeout(callback, ms))
  const clearTimer = deps.clearTimer ?? ((timer) => clearTimeout(timer))
  let unsubscribe: (() => void) | null = null
  let wasHiddenWhileRunning = false
  let watchdog: Timer | null = null

  function track(job: Promise<void>): void {
    pending.add(job)
    void job.finally(() => pending.delete(job))
  }

  async function settleCompleted(
    event: CausalProgressEvent,
    wizardWasOpen: boolean,
  ): Promise<void> {
    await Promise.allSettled([
      deps.refreshRunsAndSchedules(),
      deps.refreshCredits(),
      deps.refreshQuota(),
    ])
    if (!wizardWasOpen) deps.notifyCompleted(event.name)
  }

  async function settleFailed(event: CausalProgressEvent): Promise<void> {
    deps.dismissOverlay()
    deps.notifyFailed(event.errorMessage)
    await Promise.allSettled([deps.refreshRunsAndSchedules()])
  }

  function handleEvent(event: CausalProgressEvent): void {
    const terminal = event.phase === 'completed' || event.phase === 'failed'
    if (terminal) {
      const key = `${event.executionId}:${event.phase}`
      if (terminals.has(key)) return
      terminals.add(key)
    }

    const wizardWasOpen = deps.isWizardOpen()
    deps.applyProgressEvent(event)
    if (event.phase === 'completed') track(settleCompleted(event, wizardWasOpen))
    if (event.phase === 'failed') track(settleFailed(event))
    syncWatchdog()
  }

  function onProgress(payload: unknown): void {
    const event = parseCausalProgressEvent(payload)
    const workspaceId = deps.workspaceId()
    if (!event || workspaceId === undefined || event.workspaceId !== String(workspaceId)) return
    handleEvent(event)
  }

  /**
   * Ledger §C 5a: a refreshed `/results/` can already show the active execution as finished while
   * its terminal frame never arrived. Feed that through the same terminal path a real frame takes —
   * same toast, overlay and de-dup — so the running step resolves instead of spinning forever.
   */
  function reconcileActive(): void {
    const executionId = deps.activeExecutionId()
    const workspaceId = deps.workspaceId()
    if (executionId === null || workspaceId === undefined) return
    const run = deps.findRun(executionId)
    if (!run || run.status === 'pending' || run.status === 'running') return
    const phase: RunPhase = run.status === 'completed' ? 'completed' : 'failed'
    handleEvent({
      workspaceId: String(workspaceId),
      executionId,
      scheduleId: run.schedule_id,
      name: run.name ?? null,
      phase,
      status: run.status,
      errorMessage: null,
    })
  }

  async function refreshAndReconcile(): Promise<void> {
    await deps.refreshRunsAndSchedules()
    reconcileActive()
  }

  function clearWatchdog(): void {
    if (watchdog === null) return
    clearTimer(watchdog)
    watchdog = null
  }

  function syncWatchdog(): void {
    clearWatchdog()
    if (!unsubscribe || deps.activeExecutionId() === null) return
    watchdog = setTimer(() => {
      watchdog = null
      track(refreshAndReconcile().finally(syncWatchdog))
    }, silenceTimeoutMs)
  }

  function onVisibilityChange(): void {
    if (visibilityDocument.visibilityState === 'hidden') {
      wasHiddenWhileRunning = deps.isRunActive()
      return
    }
    if (!wasHiddenWhileRunning) return
    wasHiddenWhileRunning = false
    track(refreshAndReconcile())
  }

  return {
    start: () => {
      if (unsubscribe) return
      unsubscribe = deps.bus.on('causal_single_progress', onProgress)
      visibilityDocument.addEventListener('visibilitychange', onVisibilityChange)
      syncWatchdog()
    },
    stop: () => {
      unsubscribe?.()
      unsubscribe = null
      clearWatchdog()
      visibilityDocument.removeEventListener('visibilitychange', onVisibilityChange)
    },
    syncWatchdog,
    whenIdle: async () => {
      await Promise.all([...pending])
    },
  }
}

export function useCausalExecutionStream(wizardOpen: Readonly<Ref<boolean>>): void {
  const workspace = useWorkspace()
  const store = useImpactStore()
  const creditGuard = useCreditGuard()
  const toast = useToast()
  const { t } = useI18n()
  let runtime: CausalExecutionRuntime | null = null

  onMounted(() => {
    runtime = createCausalExecutionRuntime({
      bus: requiredBus(),
      workspaceId: () => workspace.workspaceId.value,
      isWizardOpen: () => wizardOpen.value,
      isRunActive: () => store.activeIsRunning,
      activeExecutionId: () => (store.activeIsRunning ? store.active.executionId : null),
      findRun: (executionId) => store.runs.find((run) => run.execution_id === executionId) ?? null,
      applyProgressEvent: store.applyProgressEvent,
      refreshRunsAndSchedules: () => store.refreshRunsAndSchedules({ silent: true }),
      refreshQuota: store.refreshQuota,
      refreshCredits: creditGuard.refreshBalance,
      dismissOverlay: store.dismissOverlay,
      notifyCompleted: (name) =>
        toast.success(name ?? t('impactAnalysis.toast.completedMessage'), {
          title: t('impactAnalysis.toast.completedTitle'),
          timeout: 8000,
        }),
      notifyFailed: (message) =>
        toast.error(message ?? t('impactAnalysis.toast.failedMessage'), {
          title: t('impactAnalysis.toast.failedTitle'),
        }),
    })
    runtime.start()
  })

  // A run becomes active without a frame too (`trackExecution` after a submit, the bootstrap seed):
  // the watchdog follows the store, not only the stream.
  watch(
    () => [store.activeIsRunning, store.active.executionId] as const,
    () => runtime?.syncWatchdog(),
  )

  onUnmounted(() => runtime?.stop())
}
